'use client'

import type { CSSProperties } from 'react'
import Link from 'next/link'

interface ApiUnavailablePageProps {
  error?: (Error & { digest?: string }) | null
  reset?: () => void
}

const supportEmail = 'magic@vanishingincmagic.com'

export const ApiUnavailablePage = ({
  error,
  reset,
}: ApiUnavailablePageProps) => {
  const showErrorDetails = process.env.NODE_ENV === 'development' && error

  return (
    <div className="api-error-shell">
      <style>{styles}</style>
      <main className="api-error-page" aria-labelledby="api-error-title">
        <section className="api-error-panel">
          <Link
            href="/"
            className="api-error-logo-link"
            aria-label="Vanishing Inc. home"
          >
            <img
              src="/images/vanishing-inc.svg"
              alt="Vanishing Inc."
              className="api-error-logo"
            />
          </Link>

          <div className="api-error-layout">
            <div className="api-error-copy">
              <p className="api-error-eyebrow">
                Service temporarily unavailable
              </p>
              <h1 id="api-error-title">Well this is awkward</h1>
              <p className="api-error-lede">
                We have a temporary system issue and will be back online in just
                a few minutes.
              </p>

              <div className="api-error-actions">
                {reset ? (
                  <button
                    type="button"
                    className="api-error-primary"
                    onClick={reset}
                  >
                    Try again
                  </button>
                ) : null}
                <Link className="api-error-secondary" href="/">
                  Go to homepage
                </Link>
              </div>

              <p className="api-error-support">
                Need help with an order? Email{' '}
                <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
              </p>
            </div>

            <div className="api-error-card" aria-label="Current service status">
              <div className="api-error-card-header">
                <span className="api-error-card-dot" />
                Connection check
              </div>
              <div className="api-error-meter" aria-hidden="true">
                <span style={meterStyle(72)} />
                <span style={meterStyle(42)} />
                <span style={meterStyle(54)} />
                <span style={meterStyle(26)} />
                <span style={meterStyle(38)} />
              </div>
              <p>
                We&apos;re having trouble loading the store right now. Refresh
                this page to try again.
              </p>
            </div>
          </div>

          {showErrorDetails ? (
            <div className="api-error-details">
              <p>{error.message}</p>
              {error.digest ? <p>Error ID: {error.digest}</p> : null}
            </div>
          ) : null}
        </section>
      </main>
    </div>
  )
}

function meterStyle(height: number): CSSProperties {
  return { height: `${height}%` }
}

const styles = `
  .api-error-shell {
    min-height: 100svh;
    background:
      linear-gradient(135deg, rgba(192, 64, 64, 0.08), rgba(47, 47, 47, 0) 36%),
      #f8fafc;
    color: #171717;
    font-family: "Helvetica Neue", Arial, sans-serif;
  }

  .api-error-page {
    min-height: 100svh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
  }

  .api-error-panel {
    width: min(100%, 1120px);
    background: rgba(255, 255, 255, 0.94);
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 24px 80px rgba(15, 23, 42, 0.12);
    padding: clamp(24px, 5vw, 56px);
  }

  .api-error-logo-link {
    display: inline-flex;
    margin-bottom: clamp(32px, 5vw, 56px);
  }

  .api-error-logo {
    width: min(260px, 72vw);
    height: auto;
  }

  .api-error-card-dot {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    background: #dc2626;
    box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.14);
  }

  .api-error-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(280px, 360px);
    gap: clamp(28px, 6vw, 72px);
    align-items: end;
  }

  .api-error-copy {
    max-width: 700px;
  }

  .api-error-eyebrow {
    margin: 0 0 10px;
    color: #c04040;
    font-size: 15px;
    font-weight: 800;
    text-transform: uppercase;
  }

  .api-error-copy h1 {
    margin: 0;
    max-width: 680px;
    color: #111827;
    font-size: clamp(36px, 7vw, 72px);
    line-height: 0.96;
    letter-spacing: 0;
    font-weight: 900;
  }

  .api-error-lede {
    margin: 22px 0 0;
    max-width: 620px;
    color: #4b5563;
    font-size: clamp(18px, 2.5vw, 22px);
    line-height: 1.5;
  }

  .api-error-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 32px;
  }

  .api-error-primary,
  .api-error-secondary {
    min-height: 48px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    padding: 12px 18px;
    font-size: 16px;
    font-weight: 800;
    text-decoration: none;
    cursor: pointer;
  }

  .api-error-primary {
    border: 0;
    background: #c04040;
    color: white;
  }

  .api-error-primary:hover {
    background: #a73535;
  }

  .api-error-secondary {
    border: 1px solid #d1d5db;
    background: white;
    color: #2f2f2f;
  }

  .api-error-secondary:hover {
    border-color: #9ca3af;
    background: #f9fafb;
  }

  .api-error-support {
    margin: 20px 0 0;
    color: #6b7280;
    font-size: 15px;
    line-height: 1.5;
  }

  .api-error-support a {
    color: #c04040;
    font-weight: 800;
  }

  .api-error-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #111827;
    color: white;
    padding: 24px;
    box-shadow: 0 18px 45px rgba(17, 24, 39, 0.18);
  }

  .api-error-card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #f9fafb;
    font-size: 15px;
    font-weight: 800;
  }

  .api-error-meter {
    height: 130px;
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    align-items: end;
    gap: 10px;
    margin: 28px 0 20px;
  }

  .api-error-meter span {
    display: block;
    min-height: 18px;
    border-radius: 6px 6px 2px 2px;
    background: linear-gradient(180deg, #fca5a5, #c04040);
  }

  .api-error-card p {
    margin: 0;
    color: #d1d5db;
    font-size: 15px;
    line-height: 1.5;
  }

  .api-error-details {
    margin-top: 28px;
    border: 1px solid #fecaca;
    border-radius: 8px;
    background: #fef2f2;
    padding: 16px;
    color: #991b1b;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 13px;
    overflow-wrap: anywhere;
  }

  .api-error-details p {
    margin: 0;
  }

  .api-error-details p + p {
    margin-top: 8px;
  }

  @media (max-width: 760px) {
    .api-error-page {
      align-items: stretch;
      padding: 16px;
    }

    .api-error-panel {
      padding: 24px 18px;
    }

    .api-error-layout {
      grid-template-columns: 1fr;
    }

    .api-error-card {
      padding: 18px;
    }

    .api-error-meter {
      height: 92px;
    }

    .api-error-primary,
    .api-error-secondary {
      width: 100%;
    }
  }
`
